/**
 * Sync types with backend extraction support (see api/mcl/extraction/constants.py).
 * @type {ReadonlySet<string>}
 */
export const EXTRACTION_SUPPORTED_SYNC_TYPES = new Set(["products", "providerproducts"]);

export const BUNDLES_FEATURE_CODE = "bundles";

/** Temporarily hide technical sheets from navigation menus. */
export const TECHNICAL_SHEETS_NAV_VISIBLE = false;

/** Temporarily hide production from navigation menus. */
export const PRODUCTION_NAV_VISIBLE = false;

/** Temporarily hide the provider price comparator from navigation menus. */
export const COMPARATOR_NAV_VISIBLE = false;

/** Temporarily hide replenishment from navigation menus. */
export const REPLENISH_NAV_VISIBLE = false;

/** Temporarily disable PDF import on /synchronize/products (CSV only). */
export const PRODUCTS_PDF_IMPORT_ENABLED = false;

/** Feature codes always enabled for the retailer persona (no opt-in flag). */
export const RETAILER_ALWAYS_ON_FEATURES = new Set([
    "pos_sync",
    "establishments",
    "bundles",
]);

/**
 * Menu modules always shown for retailers (used by the capability matrix).
 * @type {readonly string[]}
 */
export const RETAILER_ALWAYS_ON_MENU_MODULES = Object.freeze([
    "establishments",
    "bundles",
    "production",
]);

/**
 * @param {string} code
 * @param {string|null|undefined} persona
 * @returns {boolean}
 */
export function isRetailerAlwaysOnFeature(code, persona) {
    return ["retailer", "hospitality_group"].includes(persona) && RETAILER_ALWAYS_ON_FEATURES.has(code);
}

/**
 * @param {import('vuex').Store|{ getters?: object, state?: object }} store
 * @returns {string|null}
 */
function currentEntityPersona(store) {
    return (
        store?.getters?.["session/current_entity_persona"] ||
        store?.state?.session?.persona ||
        null
    );
}

/**
 * @param {string[]|null|undefined} modules
 * @param {string|null|undefined} persona
 * @returns {string[]}
 */
export function withRetailerAlwaysOnMenuModules(modules, persona) {
    const list = modules || [];
    if (!["retailer", "hospitality_group"].includes(persona)) {
        return list;
    }
    return Array.from(new Set([...list, ...RETAILER_ALWAYS_ON_MENU_MODULES]));
}

/**
 * @param {string} type
 * @returns {boolean}
 */
export function isExtractionSupportedForSyncType(type) {
    if (type === "products" && !PRODUCTS_PDF_IMPORT_ENABLED) {
        return false;
    }
    return EXTRACTION_SUPPORTED_SYNC_TYPES.has(type);
}

/**
 * @param {import('vuex').Store|{ getters?: object, state?: object }} store
 * @param {string} code
 * @returns {boolean}
 */
export function entityHasFeature(store, code) {
    if (isRetailerAlwaysOnFeature(code, currentEntityPersona(store))) {
        return true;
    }
    const features =
        store?.getters?.["session/current_entity_features"] ||
        store?.state?.session?.features ||
        [];
    const profile = store?.getters?.["session/current_entity_profile"];
    return features.some(
        (feature) => feature.code === code && feature.profile === profile,
    );
}

/**
 * @param {import('vuex').Store|{ getters?: object, state?: object }} store
 * @returns {boolean}
 */
export function entityHasBundlesFeature(store) {
    return entityHasFeature(store, BUNDLES_FEATURE_CODE);
}

/**
 * @param {import('vuex').Store} store
 * @returns {boolean}
 */
export function isIntelligentImportEnabled(store) {
    if (store?.state?.session?.catalog_pdf_import_enabled === true) {
        return true;
    }
    const features = store?.state?.session?.features || [];
    return features.some((feature) => feature.code === "catalog_pdf_import_enabled");
}
